import { useEffect, useState } from "react";
import { CheckCircle2, Loader2, Phone, Mail, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { axonLead } from "@/lib/axon";
import { gadsLead } from "@/lib/gads";

// AOG store sign-up. Served as the AOG Coin campaign's "Agent generated" landing page (/aog-coin; /aog redirects there).
// No auth. Excluded states are refused client- and server-side. preview=true (admin Landing tab) never submits.
const field = "w-full bg-black/70 border border-white/15 px-3 py-2.5 text-sm text-white outline-none focus:border-lime-400 transition-colors placeholder:text-white/30";
const label = "block font-mono text-[10px] uppercase tracking-widest text-white/60 mb-1";
const BUSINESS_TYPES = ["Gas station", "C-store", "Bar", "Vape shop", "Restaurant", "Grocery", "Other"];

export function AogSignupView({ campaign = null, slug = null, preview = false }) {
  const [cfg, setCfg] = useState(null);
  const [f, setF] = useState({ business_name: "", contact_name: "", phone: "", email: "", address: "", city: "", state: "", business_type: "", consent_contact: false });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [err, setErr] = useState("");
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  useEffect(() => { api.get("/public/aog-signup/config").then((r) => setCfg(r.data)).catch(() => setErr("Could not load the form. Please call or email us.")); }, []);

  const picked = cfg?.states.find((s) => s.abbr === f.state);
  const blocked = picked && !picked.eligible;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (blocked) return;
    for (const k of ["business_name", "contact_name", "phone", "email", "address", "city", "state", "business_type"]) {
      if (!String(f[k]).trim()) { setErr("Please fill in every field."); return; }
    }
    if (preview) { toast.info("Preview only — the live page will send this."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/public/aog-signup", { ...f, notes: null, campaign_slug: slug || campaign?.slug || null });
      axonLead("aog_signup"); gadsLead();
      setDone(data);
    } catch (ex) {
      const d = ex?.response?.data?.detail;
      setErr((d && (d.message || (typeof d === "string" ? d : null))) || "Something went wrong — please call or email us.");
    } finally { setBusy(false); }
  };

  return (
    <div className={`${preview ? "" : "min-h-screen"} bg-[#080808] text-white`} data-testid="aog-signup-page" data-preview={preview ? "1" : undefined}>
      <div className="h-2 bg-lime-400" />
      <div className="max-w-5xl mx-auto px-5 py-10 grid grid-cols-1 lg:grid-cols-5 gap-10">
        <div className="lg:col-span-2 space-y-6">
          <div className="font-mono text-xs font-bold tracking-widest text-lime-400">21+ · SWEEPSTAKES GAMING · NO PURCHASE NECESSARY</div>
          <div>
            <h1 className="text-4xl sm:text-5xl font-black leading-none">AOG COIN</h1>
            <div className="font-mono text-xs tracking-widest text-white/50 mt-1">MARKETED BY {cfg?.partner?.toUpperCase() || "ARENA GAMES"}</div>
          </div>
          <h2 className="text-base md:text-lg font-bold text-lime-400" data-testid="aog-signup-headline">A free {cfg?.kiosk || "AOG Sweeps game kiosk"} for your counter — nothing to buy.</h2>
          <div className="bg-lime-400 text-black px-5 py-4" data-testid="aog-signup-lucky-cta">
            <div className="font-mono text-[10px] font-bold tracking-widest uppercase">Questions? Lucky answers 24/7</div>
            <a href={`tel:${cfg?.lucky_tel || "+13252214234"}`} className="block text-2xl sm:text-3xl font-black leading-tight mt-1 hover:underline" data-testid="aog-signup-lucky-tel">{cfg?.lucky_cta || "Call or text Lucky · (325) 221-4234"}</a>
            <div className="text-xs font-semibold mt-1 opacity-80">or <a href={`sms:${cfg?.lucky_tel || "+13252214234"}`} className="underline" data-testid="aog-signup-lucky-sms">text {cfg?.lucky_phone || "(325) 221-4234"}</a> · or sign up below</div>
          </div>
          <ul className="space-y-3 text-sm text-white/85">
            {(cfg?.bullets || []).map((b, i) => <li key={i} className="flex gap-3"><span className="mt-2 w-2 h-2 rounded-full bg-lime-400 shrink-0" />{b}</li>)}
          </ul>
          <div className="border border-lime-400/40 bg-lime-400/[0.05] p-4" data-testid="aog-signup-offer">
            <div className="font-bold text-lime-400">{cfg?.delivery_cta || "Call, email or sign up"}</div>
            <div className="text-sm text-white/80 mt-1">{cfg?.delivery_line || "We deliver the kiosk tablet to your store · Eligible states only"}</div>
            <div className="mt-3 space-y-1 text-sm">
              {cfg?.phone && <a href={`tel:${cfg.phone}`} data-testid="aog-signup-phone" className="flex items-center gap-2 hover:text-lime-400"><Phone className="w-4 h-4" /> Lucky · Arcade Ops · {cfg.lucky_phone || cfg.phone}</a>}
              {cfg?.email && <a href={`mailto:${cfg.email}`} data-testid="aog-signup-email" className="flex items-center gap-2 hover:text-lime-400"><Mail className="w-4 h-4" /> {cfg.email}</a>}
            </div>
          </div>
        </div>

        <div className="lg:col-span-3">
          {done ? (
            <div className="border border-lime-400 bg-lime-400/[0.06] p-8 space-y-3" data-testid="aog-signup-success">
              <CheckCircle2 className="w-10 h-10 text-lime-400" />
              <div className="text-xl font-bold">You're on the list.</div>
              <p className="text-sm text-white/85">{done.message}</p>
              <p className="text-xs text-white/50 font-mono">Reference {done.id.slice(0, 8)}</p>
            </div>
          ) : (
            <form onSubmit={submit} className="border border-white/10 bg-white/[0.02] p-6 space-y-4" data-testid="aog-signup-form">
              <div className="font-mono text-xs uppercase tracking-widest text-lime-400">Sign up your store</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><label className={label}>Business name</label><input data-testid="aog-signup-business" className={field} value={f.business_name} onChange={set("business_name")} placeholder="Sunset Tavern" /></div>
                <div><label className={label}>Your name</label><input data-testid="aog-signup-contact" className={field} value={f.contact_name} onChange={set("contact_name")} placeholder="Alex Rivera" /></div>
                <div><label className={label}>Phone</label><input data-testid="aog-signup-phone-input" className={field} value={f.phone} onChange={set("phone")} placeholder="(404) 555-0123" inputMode="tel" /></div>
                <div><label className={label}>Email</label><input data-testid="aog-signup-email-input" className={field} type="email" value={f.email} onChange={set("email")} placeholder="you@store.com" /></div>
                <div className="sm:col-span-2"><label className={label}>Street address</label><input data-testid="aog-signup-address" className={field} value={f.address} onChange={set("address")} placeholder="123 Main St" /></div>
                <div><label className={label}>City</label><input data-testid="aog-signup-city" className={field} value={f.city} onChange={set("city")} placeholder="Atlanta" /></div>
                <div>
                  <label className={label}>State</label>
                  <select data-testid="aog-signup-state" className={field} value={f.state} onChange={set("state")}>
                    <option value="">Select…</option>
                    {(cfg?.states || []).map((s) => <option key={s.abbr} value={s.abbr}>{s.name}{s.eligible ? "" : " — not available yet"}</option>)}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className={label} htmlFor="aog-business-type">Business type</label>
                  <select id="aog-business-type" data-testid="aog-signup-business-type" className={field} value={f.business_type} onChange={set("business_type")}>
                    <option value="">Select…</option>
                    {BUSINESS_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>
              {blocked && (
                <div className="border border-amber-400/50 bg-amber-400/10 px-4 py-3 text-sm text-amber-200" data-testid="aog-signup-state-blocked">
                  AOG isn't available in {picked.name} yet, so we can't take a sign-up from there. Nothing has been saved.
                </div>
              )}
              <label className="flex items-start gap-3 text-sm text-white/85 cursor-pointer">
                <input type="checkbox" data-testid="aog-signup-consent" checked={f.consent_contact} onChange={set("consent_contact")} className="mt-1 accent-lime-400" />
                Yes, you can call or text me about the kiosk.
              </label>
              {err && <div className="text-sm text-red-400" data-testid="aog-signup-error">{err}</div>}
              <button type="submit" disabled={busy || blocked || !cfg} data-testid="aog-signup-submit"
                className="w-full bg-lime-400 text-black font-black uppercase tracking-widest py-3 hover:bg-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Sign up — deliver my kiosk
              </button>
              <p className="text-[11px] text-white/45">By signing up you're asking us to contact you about the kiosk. The kiosk is for registration only — it is not a gaming machine.</p>
            </form>
          )}
        </div>
      </div>
      <div className="max-w-5xl mx-auto px-5 pb-12">
        <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-white/40 mb-2"><ShieldCheck className="w-3.5 h-3.5" /> Legal</div>
        <p className="text-[11px] leading-relaxed text-white/50 font-mono" data-testid="aog-signup-legal">{cfg?.legal_block}</p>
        <p className="mt-2 text-[11px] leading-relaxed text-white/70 font-mono font-bold" data-testid="aog-legal-footer">No purchase necessary. Void where prohibited. 21+.</p>
      </div>
      <div className="h-2 bg-lime-400" />
    </div>
  );
}

// Standalone page (kept for direct reuse; the /aog route itself redirects to the AOG Coin campaign page).
export default function AogSignup() {
  return <AogSignupView />;
}
